import { getColombiaHoliday } from './colombiaHolidays';

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export const DEFAULT_DAILY_RATES = Object.freeze({
  weekdays: 0,
  saturday: 0,
  sunday: 0,
});

const normalizeRates = (employee = {}) => {
  const raw = employee?.dailyRates || {};
  return {
    weekdays: Number(raw.weekdays ?? 0),
    saturday: Number(raw.saturday ?? 0),
    sunday: Number(raw.sunday ?? 0),
  };
};

export const getDayName = (dateString) => {
  if (!dateString) return '';
  const ref = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(ref.getTime())) return '';
  return DAY_NAMES[ref.getDay()];
};

export const getRateForDate = (employee, dateString) => {
  const ref = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(ref.getTime())) return 0;
  const day = ref.getDay();
  const rates = normalizeRates(employee);
  // En ValenPOS la tarifa `sunday` representa "Domingo / Festivo".
  // Un festivo nacional colombiano tiene prioridad aunque caiga entre semana o sábado.
  if (getColombiaHoliday(dateString) || day === 0) return rates.sunday;
  if (day === 6) return rates.saturday;
  return rates.weekdays;
};

const generateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
  const dates = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
};

export const buildDailySummary = ({
  employee,
  attendanceMap = {},
  startDate,
  endDate,
  extras = 0,
  bonuses = 0,
  settings = {},
}) => {
  if (!employee || !startDate || !endDate) return null;
  const dates = generateDateRange(startDate, endDate);
  const rows = [];
  let daysPresent = 0;
  let daysAbsent = 0;
  let salarySubtotal = 0;
  let auxSubtotal = 0;

  const smmlv = Number(settings?.smmlv || 0);
  const auxTransportMonthly = Number(settings?.auxTransporte || 0);
  // Para pagos exclusivamente diarios no se reconoce auxilio de transporte.
  const auxEligible = false;
  const auxPerDay = 0;

  for (const date of dates) {
    const attendance = attendanceMap?.[date];
    const status = attendance?.status === 'present' ? 'present' : 'absent';
    const holiday = getColombiaHoliday(date);
    const rate = getRateForDate(employee, date);
    const salaryPaid = status === 'present' ? rate : 0;
    const aux = status === 'present' ? auxPerDay : 0;
    const paid = salaryPaid + aux;
    if (status === 'present') daysPresent += 1;
    else daysAbsent += 1;
    salarySubtotal += salaryPaid;
    auxSubtotal += aux;
    rows.push({
      date,
      dayName: getDayName(date),
      isHoliday: !!holiday,
      holidayName: holiday?.name || null,
      holidaySource: holiday?.source || null,
      holidayOriginalDate: holiday?.originalDate || null,
      holidayMoved: !!holiday?.moved,
      status,
      rate,
      salaryPaid,
      aux,
      paid,
      attendanceId: attendance?.id || null,
      recordedBy: attendance?.recordedBy || null,
      updatedAt: normalizeTimestamp(attendance?.updatedAt || attendance?.createdAt || null),
    });
  }

  const extrasValue = Number(extras || 0);
  const bonusesValue = Number(bonuses || 0);
  const calculatedTotal = salarySubtotal + auxSubtotal;

  return {
    days: rows,
    totals: {
      daysPresent,
      daysAbsent,
      salarySubtotal,
      auxTransport: auxSubtotal,
      calculatedTotal,
      extras: extrasValue,
      bonuses: bonusesValue,
      final: calculatedTotal + extrasValue + bonusesValue,
      eligibleAux: auxEligible,
      auxPerDay,
    },
  };
};

export default {
  DEFAULT_DAILY_RATES,
  getDayName,
  getRateForDate,
  buildDailySummary,
};
const normalizeTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().toISOString();
    } catch (_) {
      return null;
    }
  }
  return null;
};
